// Modules
import cn from 'classnames';

// Types
import type { TTextFieldProps, TTextFieldStatus } from './TextField.type';

// Styles
import styles from './styles/styles.module.scss';

// Components
import Icon from '../Icon';

// Helpers
import { generateID } from '../../helpers/common';

// Constants
import { TEXT_FIELD_DEFAULT_PROPS, TEXT_FIELD_STATUS, TEXT_FIELD_VARIANT } from './TextField.constant';
import { ICON_NAME } from '../Icon/Icon.constant';

const getCaptionIcon = (status: TTextFieldStatus) => {
	switch (status) {
		case TEXT_FIELD_STATUS.error:
			return ICON_NAME['error-triangle'];
		case TEXT_FIELD_STATUS.success:
			return ICON_NAME['success-circle'];
		default:
			return ICON_NAME['info-circle'];
	}
};

const TextField = (props: TTextFieldProps) => {
	const {
		block,
		caption,
		captionClassName,
		className,
		disabled,
		hint,
		hintClassName,
		iconLeft,
		iconRight,
		id,
		inputClassName,
		label,
		labelClassName,
		placeholder,
		prefix,
		size = TEXT_FIELD_DEFAULT_PROPS.size,
		status = TEXT_FIELD_DEFAULT_PROPS.status,
		suffix,
		variant = TEXT_FIELD_DEFAULT_PROPS.variant,
		...restProps
	} = props;
	const isOutline = variant === TEXT_FIELD_VARIANT.outline;

	const textFieldCN = cn(styles.legion_text_field, className, styles[size!], styles[status!], {
		[styles.block]: block,
		[styles.disabled]: disabled,
		[styles[variant!]]: variant,
	});
	const captionCN = cn(styles.text_field_caption, captionClassName);
	const hintCN = cn(styles.text_field_hint, hintClassName);
	const inputCN = cn(styles.input_wrapper, inputClassName);
	const labelCN = cn(styles.text_field_label, labelClassName);


	const inputId = id || `legion_text_field_${generateID()}`;
	const captionIcon = getCaptionIcon(status!);

	return (
		<div className={textFieldCN}>
			{label && (
				<label
					htmlFor={inputId}
					className={labelCN}
				>
					{label}
				</label>
			)}
			<div className={inputCN}>
				{prefix && (
					<span className={styles.input_prefix}>
						{prefix}
					</span>
				)}
				{iconLeft && (
					<span className={styles.icon_left}>
						{iconLeft}
					</span>
				)}
				<input
					className={styles.text_field_input}
					id={inputId}
					placeholder={placeholder}
					disabled={disabled}
					{...restProps}
				/>
				{isOutline && (
					<svg
						xmlns={'http://www.w3.org/2000/svg'}
					>
						<rect height={'100%'} width={'100%'} fill={'none'}/>
					</svg>
				)}
				{iconRight && (
					<span className={styles.icon_right}>
						{iconRight}
					</span>
				)}
				{suffix && (
					<span className={styles.input_suffix}>
						{suffix}
					</span>
				)}
			</div>
			{caption && (
				<p className={captionCN}>
					<Icon className={styles.caption_icon} icon={captionIcon}/>
					{caption}
				</p>
			)}
			{hint && (
				<p className={hintCN}>
					{hint}
				</p>
			)}
		</div>
	);
}

export default TextField;
